/**
 * @fileoverview Contexto responsável por carregar, sincronizar e expor as filas de atendimento.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { SocketContext } from "../Socket/SocketContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const QueuesContext = createContext();

export const QueuesProvider = ({ children }) => {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketManager = useContext(SocketContext);

  // Função para buscar filas do backend
  const fetchQueues = useCallback(async () => {
    // Não tentar buscar filas em rotas públicas
    const publicRoutes = ['/login', '/signup'];
    const isPublicRoute = publicRoutes.some(route =>
      window.location.pathname.startsWith(route)
    );

    if (isPublicRoute) {
      setLoading(false);
      return [];
    }

    // Não tentar buscar filas se não houver token (usuário não autenticado)
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/queue");
      setQueues(data);
      return data;
    } catch (err) {
      setError(err);
      toastError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Função para recarregar filas (útil para forçar refresh)
  const refetchQueues = useCallback(() => {
    return fetchQueues();
  }, [fetchQueues]);

  // Buscar filas na inicialização
  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  // Listener WebSocket para atualizações em tempo real
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId || !socketManager) return;

    const socket = socketManager.getSocket(companyId);

    const handleQueueUpdate = (data) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log("📡 [QueuesContext] WebSocket update:", data.action);
      }

      if (data.action === "update" || data.action === "create") {
        setQueues((prevQueues) => {
          const queueIndex = prevQueues.findIndex((q) => q.id === data.queue.id);

          if (queueIndex !== -1) {
            // Atualizar fila existente
            const newQueues = [...prevQueues];
            newQueues[queueIndex] = data.queue;
            return newQueues;
          } else {
            // Adicionar nova fila
            return [...prevQueues, data.queue];
          }
        });
      }

      if (data.action === "delete") {
        setQueues((prevQueues) =>
          prevQueues.filter((q) => q.id !== data.queueId)
        );
      }
    };

    socket.on(`company-${companyId}-queue`, handleQueueUpdate);

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const value = {
    queues,
    loading,
    error,
    refetchQueues,
  };

  return (
    <QueuesContext.Provider value={value}>
      {children}
    </QueuesContext.Provider>
  );
};

// Hook customizado para usar o contexto
export const useQueues = () => {
  const context = useContext(QueuesContext);

  if (!context) {
    throw new Error("useQueues deve ser usado dentro de um QueuesProvider");
  }

  return context;
};

export default QueuesContext;
